import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { AgentSelector } from "@/components/AgentSelector";
import {
  SUMMARIZE_LAST_AGENT_KEY,
  getDefaultAgentId,
  setLastUsedAgentId,
} from "@/lib/threadActionAgents";

export type SummarizeScope = "last_response" | "entire_thread";

export interface SummarizeArchiveOptions {
  agentId: string;
  scope: SummarizeScope;
  archive: boolean;
}

interface SummarizeArchiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs the agent, creates the document, optionally archives. Should throw on failure. */
  onSummarize: (options: SummarizeArchiveOptions) => Promise<void>;
  /** Disables the "last response" scope when the thread has no assistant message. */
  hasAssistantMessage: boolean;
}

export function SummarizeArchiveDialog({
  open,
  onOpenChange,
  onSummarize,
  hasAssistantMessage,
}: SummarizeArchiveDialogProps) {
  const [agentId, setAgentId] = useState<string | null>(null);
  const [scope, setScope] = useState<SummarizeScope>("last_response");
  const [archive, setArchive] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (open) {
      setAgentId(
        (prev) => prev ?? getDefaultAgentId(SUMMARIZE_LAST_AGENT_KEY, /summar|markdown|writer/i)
      );
      setArchive(false);
      if (!hasAssistantMessage) setScope("entire_thread");
    }
  }, [open, hasAssistantMessage]);

  const handleSummarize = async () => {
    if (!agentId || running) return;
    setRunning(true);
    try {
      setLastUsedAgentId(SUMMARIZE_LAST_AGENT_KEY, agentId);
      await onSummarize({ agentId, scope, archive });
      onOpenChange(false);
    } catch {
      // Error toast is shown by the caller; keep the dialog open for retry (G4).
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !running && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Summarize &amp; Archive</DialogTitle>
          <DialogDescription>
            Create a markdown summary document in the current space and topic, and optionally
            archive this thread.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-5">
          <div className="space-y-2">
            <Label>Select Agent</Label>
            <AgentSelector
              value={agentId}
              onValueChange={setAgentId}
              isActive={open}
              disabled={running}
            />
            {!agentId && (
              <p className="text-xs text-muted-foreground">
                No agents available — pick a built-in agent above or create one in the Agents hub.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Scope</Label>
            <RadioGroup
              value={scope}
              onValueChange={(value) => setScope(value as SummarizeScope)}
              disabled={running}
              className="gap-2"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="last_response"
                  id="summarize-scope-last"
                  disabled={!hasAssistantMessage}
                  data-testid="radio-scope-last-response"
                />
                <Label htmlFor="summarize-scope-last" className="font-normal cursor-pointer">
                  Last response
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="entire_thread"
                  id="summarize-scope-thread"
                  data-testid="radio-scope-entire-thread"
                />
                <Label htmlFor="summarize-scope-thread" className="font-normal cursor-pointer">
                  Entire thread
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="summarize-archive-checkbox"
              checked={archive}
              onCheckedChange={(checked) => setArchive(checked === true)}
              disabled={running}
              data-testid="checkbox-archive-thread"
            />
            <div className="space-y-1">
              <Label htmlFor="summarize-archive-checkbox" className="font-normal cursor-pointer">
                Archive thread after summarizing
              </Label>
              <p className="text-xs text-muted-foreground">
                The thread moves to Archived chats and is removed from AI search. You can restore
                it anytime.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
            data-testid="button-summarize-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSummarize}
            disabled={!agentId || running}
            data-testid="button-summarize-run"
          >
            {running ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {archive ? "Summarizing & archiving..." : "Summarizing..."}
              </>
            ) : archive ? (
              "Summarize & Archive"
            ) : (
              "Summarize"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
